import React, { useState, useEffect, useRef } from 'react';
import { Text, View, Image, Dimensions, Animated, Easing, Linking, TouchableOpacity } from 'react-native';

import { Camera } from 'react-native-vision-camera';

import i18n from 'i18n-js';
import { getStatusBarHeight } from 'react-native-status-bar-height';

import { isPhone, getSecureValue, getSelectedCameraDevice } from '@helpers';
import ScanWrapper from '@assets/img/ScanWrapper.png';
import ScanWrapperTablet from '@assets/img/ScanWrapperTablet.png';

import { font } from '@styles/fonts';
import { scale, notchTop, Element, bottomBarHeight } from '@components/frame';
import Svg from '@styles/icons/svg';
import Button from '@components/button/index';

export default ({ C, scanned, handleBarCodeScanned, setShowScanner }) => {
  const [hasPermission, setHasPermission] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(true);
  const [device, setDevice] = useState(null);
  const statusBar = getStatusBarHeight();
  const devWidth = Dimensions.get('window').width;
  const devHeight = Dimensions.get('window').height;

  const askAgain = () => {
    Linking.openSettings();
  };

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const startAnimation = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 1000,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();
  };

  const validateCameraPermission = async (status) => {
    switch (status) {
      case 'not-determined': {
        const newCameraPermission = await Camera.requestCameraPermission();
        return await validateCameraPermission(newCameraPermission);
      }
      case 'granted': {
        const selectedDevice = await getSelectedCameraDevice();

        setDevice(selectedDevice);
        setHasPermission(true);
        setTimeout(() => {
          startAnimation();
        }, 2000);
        break;
      }
      default: {
        setHasPermission(false);
      }
    }
  };

  useEffect(() => {
    (async () => {
      const auth = getSecureValue('token');
      setIsAuthorized(auth);

      const status = Camera.getCameraPermissionStatus();
      await validateCameraPermission(status);
      setLoading(false);
    })();
  }, []);

  if (hasPermission === false || hasPermission === null) {
    return (
      <View style={{ backgroundColor: C('Background/Light/System'), width: '100%', height: '100%' }}>
        <Element
          {...{
            config: {
              position: {
                top: notchTop,
              },
              width: '100%',
              height: '100%',
            },
          }}
        >
          <Element
            {...{
              config: {
                position: {
                  top: 9,
                  right: 0,
                },
                width: 54,
                height: 54,
                justifyContent: 'center',
                alignItems: 'center',
                alignContent: 'center',
              },
            }}
          >
            <TouchableOpacity
              onPress={() => setShowScanner(false)}
              activeOpacity={1}
              style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', alignContent: 'center' }}
            >
              <View
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  alignContent: 'center',
                  width: scale(27),
                  height: scale(27),
                  backgroundColor: C('Button/Light/Tertiary'),
                  borderRadius: scale(27),
                }}
              >
                <Svg icon="close" width={scale(11)} height={scale(11)} color="Button/Light/Tertiary Text" />
              </View>
            </TouchableOpacity>
          </Element>
          <Element
            {...{
              config: {
                position: {
                  top: 302,
                },
                width: '100%',
              },
            }}
          >
            <Text style={[font['Large Title'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>{i18n.t('Scan QR Code')}</Text>
          </Element>
          <Element
            {...{
              config: {
                position: {
                  top: 355,
                },
                width: '100%',
              },
            }}
          >
            <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary'), textAlign: 'center' }]}>
              {i18n.t('Allow Smart Factory to access your camera.')}
            </Text>
          </Element>
          <Element
            {...{
              config: {
                position: {
                  top: 401,
                },
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              },
            }}
          >
            <View style={{ width: isPhone ? '100%' : scale(382), paddingHorizontal: isPhone ? scale(29) : 0 }}>
              <Button
                {...{
                  name: i18n.t('Enable access'),
                  onPress: askAgain,
                }}
              />
            </View>
          </Element>
        </Element>
      </View>
    );
  }

  return (
    <View style={[{ backgroundColor: C('Overlay/Light/Dim strong'), flex: 1 }]}>
      <Image
        style={[
          {
            width: devWidth,
            height: devHeight - (isAuthorized ? bottomBarHeight : 0),
            position: 'absolute',
            zIndex: 10,
          },
        ]}
        source={!isPhone ? ScanWrapperTablet : ScanWrapper}
      />
      <View style={{ position: 'absolute', zIndex: 20, width: '100%' }}>
        <TouchableOpacity
          onPress={() => setShowScanner(false)}
          style={{
            marginTop: statusBar + 26,
            marginLeft: scale(24),
            height: 50,
            width: 100,
          }}
        >
          <Text
            style={[
              font.Headline,
              {
                color: 'white',
                display: 'flex',
                justifyContent: 'center',
              },
            ]}
          >
            {i18n.t('Cancel')}
          </Text>
        </TouchableOpacity>
        <View style={[{ top: devHeight / 6.6 - (statusBar + 50) }]}>
          <Text style={[font['Title 2'], { color: 'white', textAlign: 'center' }]}>{i18n.t('Scan a QR code')}</Text>
        </View>
        <Animated.View
          style={[
            {
              top: devHeight / 1.2,
              opacity: fadeAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 1],
              }),
              position: 'absolute',
              zIndex: 30,
              width: '100%',
            },
          ]}
        >
          <Text style={[font.Body, { textAlign: 'center', color: 'white' }]}>{i18n.t('Position your camera…')}</Text>
        </Animated.View>
      </View>
      <Camera
        device={device}
        style={{ width: '100%', height: '100%' }}
        codeScanner={{
          codeTypes: ['qr'],
          onCodeScanned: (codes) => {
            if (scanned.current || !onPage) {
            } else {
              handleBarCodeScanned(codes[0]);
            }
          },
        }}
        isActive={true}
      />
    </View>
  );
};
